import React, { useState } from 'react';
import { styled } from '@compiled/react';
import { ErrorMessage, Field } from '@atlaskit/form';
import BookIcon from '@atlaskit/icon/glyph/book';
import HipchatLobbyIcon from '@atlaskit/icon/glyph/hipchat/lobby';
import QuestionsIcon from '@atlaskit/icon/glyph/questions';
import Select, { type ValueType } from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import {
	MAX_URL_LENGTH,
	MAX_TITLE_LENGTH,
	MAX_DESCRIPTION_LENGTH,
	MIN_ROWS_DESCRIPTION,
	ERROR_TYPES,
	MODES,
} from '../../../../common/constants';
import type { ExternalLinkObject } from '../../../../common/types';
import { messages } from './messages';
import { validateTextField, validateSelectField } from './utils';

export type AddLinkFormProps = {
	isModified: boolean;
	link: ExternalLinkObject | null;
	mode: string;
	setIsModified: (isModified: boolean) => void;
};

export const AddLinkForm = ({ link, mode, isModified, setIsModified }: AddLinkFormProps) => {
	const { formatMessage } = useIntl();

	const linkTypeOptions = [
		{
			value: 'knowledge',
			label: formatMessage(messages.externalLinksLinkTypeKnowledge),
		},
		{
			value: 'request-form',
			label: formatMessage(messages.externalLinksLinkTypeRequestForm),
		},
		{
			value: 'channel',
			label: formatMessage(messages.externalLinksLinkTypeChannels),
		},
	].filter(Boolean);

	const defaultValues =
		mode === MODES.EDIT && link !== null
			? {
					resourceType: linkTypeOptions.find(
						(linkTypeOption) => linkTypeOption?.value === link.resourceType,
					),
					link: link.link,
					title: link.title,
					description: link.description,
				}
			: {
					resourceType: null,
					link: '',
					title: '',
					description: '',
				};

	const [isChannelSelected, setIsChannelSelected] = useState<boolean>(
		// @ts-expect-error - TS2367 - This condition will always return 'false' since the types '{ value: string; label: string; } | null | undefined' and 'string' have no overlap.
		defaultValues.resourceType === 'channel',
	);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const formatLinkTypeOptions = (option: any) => {
		switch (option.value) {
			case 'knowledge': {
				return (
					<IconWrapper>
						<BookIcon label="" />
						<IconMessageWrapper>{option.label}</IconMessageWrapper>
					</IconWrapper>
				);
			}
			case 'request-form': {
				return (
					<IconWrapper>
						<HipchatLobbyIcon label="" />
						<IconMessageWrapper>{option.label}</IconMessageWrapper>
					</IconWrapper>
				);
			}
			case 'channel':
			default: {
				return (
					<IconWrapper>
						<QuestionsIcon label="" />
						<IconMessageWrapper>{option.label}</IconMessageWrapper>
					</IconWrapper>
				);
			}
		}
	};

	// @ts-expect-error - TS7006 - Parameter 'fieldProps' implicitly has an 'any' type.
	const fieldErrorMessage = (valid: boolean, error: undefined | string, fieldProps) => {
		const showError = !valid && error !== undefined;
		let errorMessage = null;

		switch (fieldProps.name) {
			case 'resourceType':
				errorMessage = formatMessage(messages.externalLinksLinkTypeIsRequired);
				break;
			case 'link':
				if (error === ERROR_TYPES.CHAR_LIMIT_EXCEEDED) {
					errorMessage = formatMessage(messages.externalLinksLinkCharLimitExceeded, {
						limit: MAX_URL_LENGTH,
					});
				} else {
					errorMessage = formatMessage(messages.externalLinksLinkUrlIsRequired);
				}
				break;
			case 'title':
				if (error === ERROR_TYPES.CHAR_LIMIT_EXCEEDED) {
					errorMessage = formatMessage(messages.externalLinksTitleCharLimitExceeded, {
						limit: MAX_TITLE_LENGTH,
					});
				} else {
					errorMessage = formatMessage(messages.externalLinksLinkTitleIsRequired);
				}
				break;
			case 'description':
				if (error === ERROR_TYPES.CHAR_LIMIT_EXCEEDED) {
					errorMessage = formatMessage(messages.externalLinksDescriptionCharLimitExceeded, {
						limit: MAX_DESCRIPTION_LENGTH,
					});
				} else {
					errorMessage = formatMessage(messages.externalLinksLinkDescriptionIsRequired);
				}
				break;
			default:
				errorMessage = null;
		}

		return showError ? <ErrorMessage>{errorMessage}</ErrorMessage> : null;
	};

	return (
		<>
			<Field
				autoFocus
				aria-required
				name="resourceType"
				defaultValue={defaultValues.resourceType}
				label={formatMessage(messages.externalLinksLinkType)}
				// @ts-expect-error - TS2322 - Type '(linkType: {    value: string;} | null) => string | undefined' is not assignable to type '(value: { value: string; label: string; } | null | undefined, formState: Object, fieldState: Meta) => string | void | Promise<string | void>'.
				validate={validateSelectField}
				isRequired
				isDisabled={mode === MODES.EDIT}
			>
				{({ fieldProps: { id, onChange, ...rest }, error, valid }) => (
					<>
						<Select
							placeholder={formatMessage(messages.externalLinksLinkTypePlaceholder)}
							inputId={id}
							{...rest}
							options={linkTypeOptions}
							onChange={(option: ValueType<{ value: string; label: string }>) => {
								setIsChannelSelected(option?.value === 'channel');
								onChange(option);
								!isModified && setIsModified(true);
							}}
							validationState={error !== undefined ? 'error' : 'default'}
							formatOptionLabel={formatLinkTypeOptions}
							testId="servicedesk-external-links.ui.links.add-link-modal.add-link-form.select-resource-type"
						/>
						{fieldErrorMessage(valid, error, rest)}
					</>
				)}
			</Field>
			<Field
				aria-required
				name="link"
				defaultValue={defaultValues.link}
				label={formatMessage(messages.externalLinksLinkUrl)}
				validate={(value) => validateTextField('link', value)}
				isRequired
			>
				{({ fieldProps: { onChange, ...rest }, error, valid }) => (
					<>
						<TextField
							{...rest}
							placeholder={formatMessage(
								isChannelSelected
									? messages.externalLinksLinkChannelPlaceholder
									: messages.externalLinksLinkUrlPlaceholder,
							)}
							testId="servicedesk-external-links.ui.links.add-link-modal.add-link-form.link-url"
							onChange={(option) => {
								onChange(option);
								!isModified && setIsModified(true);
							}}
						/>
						{fieldErrorMessage(valid, error, rest)}
					</>
				)}
			</Field>
			<Field
				aria-required
				name="title"
				defaultValue={defaultValues.title}
				label={formatMessage(messages.externalLinksTitle)}
				validate={(value) => validateTextField('title', value)}
				isRequired
			>
				{({ fieldProps: { onChange, ...rest }, error, valid }) => (
					<>
						<TextField
							{...rest}
							placeholder={formatMessage(messages.externalLinksTitlePlaceholder)}
							testId="servicedesk-external-links.ui.links.add-link-modal.add-link-form.title"
							onChange={(option) => {
								onChange(option);
								!isModified && setIsModified(true);
							}}
						/>
						{fieldErrorMessage(valid, error, rest)}
					</>
				)}
			</Field>

			<Field
				aria-required
				name="description"
				defaultValue={defaultValues.description}
				label={formatMessage(messages.externalLinksDescription)}
				validate={(value) => validateTextField('description', value)}
				isRequired
			>
				{({ fieldProps: { onChange, ...rest }, error, valid }) => (
					<>
						<TextArea
							{...rest}
							minimumRows={MIN_ROWS_DESCRIPTION}
							placeholder={formatMessage(messages.externalLinksDescriptionPlaceholder)}
							resize="vertical"
							testId="servicedesk-external-links.ui.links.add-link-modal.add-link-form.description"
							onChange={(option) => {
								// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								onChange(option as unknown as React.FormEvent<HTMLInputElement>);
								!isModified && setIsModified(true);
							}}
						/>
						{fieldErrorMessage(valid, error, rest)}
					</>
				)}
			</Field>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconMessageWrapper = styled.div({
	marginLeft: token('space.100', '8px'),
	marginRight: token('space.050', '4px'),
});
